import { gql, type TypedDocumentNode } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Market } from '@sorare/core/src/atoms/icons/Market';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Grid } from '@sorare/core/src/atoms/layout/grid';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { navLabels } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { PackWithBuyButton } from '@sorare/marketplace/src/components/commonCard/BuyPacks/PackWithBuyButton';

import { MissionSection } from '#src/components/missions/MissionSection/index.tsx';

import type { TaskCardPacks_taskInterface } from './__generated__/index.graphql.ts';

const CardPackWrapper = styled(Vertical)`
  position: relative;
  background: var(--c-nl-200);
  border-radius: var(--unit);
  border: 1px solid var(--c-nd-50);
  padding: var(--unit);
`;
type Props = {
  task: TaskCardPacks_taskInterface;
};

export const TaskCardPacks = ({ task }: Props) => {
  if (
    !isType(task, 'CardsCountTargetTask') ||
    !task.relatedPackConfigs?.length
  ) {
    return null;
  }

  return (
    <MissionSection
      icon={<Market size={2} />}
      iconColor="var(--c-white)"
      title={<FormattedMessage {...navLabels.market} />}
    >
      <BodyM>
        <FormattedMessage defaultMessage="Buy a pack to complete your mission!" />
      </BodyM>
      <Grid>
        {task.relatedPackConfigs?.map(cardPack => (
          <CardPackWrapper key={cardPack.slug}>
            <PackWithBuyButton
              cardPack={cardPack}
              cardPackGroupSlug={cardPack.cardPackGroup?.slug || ''}
            />
          </CardPackWrapper>
        ))}
      </Grid>
    </MissionSection>
  );
};

TaskCardPacks.fragments = {
  taskInterface: gql`
    fragment TaskCardPacks_taskInterface on TaskInterface {
      id
      ... on CardsCountTargetTask {
        id
        cardSet {
          slug
          branding {
            id
            colors {
              surfacePrimary
            }
          }
        }
        relatedPackConfigs {
          slug
          cardPackGroup {
            slug
          }
          ...PackWithBuyButton_cardPack
        }
      }
    }
    ${PackWithBuyButton.fragments.cardPack}
  ` as TypedDocumentNode<TaskCardPacks_taskInterface>,
};
